import React, {
  useCallback, useMemo, useRef, useEffect,
} from 'react';
import { useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { NavHashLink } from 'react-router-hash-link';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Dropdown from 'rc-dropdown';
import 'rc-dropdown/assets/index.css';
import Menu, { Item as MenuItem } from 'rc-menu';

import { ReactComponent as NationalMembershipIcon } from 'assets/svg/national-membership.svg';
import { ReactComponent as SearchIcon } from 'assets/images/svg/search.svg';
import { ReactComponent as LocationIcon } from 'assets/images/svg/location.svg';
import { ReactComponent as SpecialsIcon } from 'assets/images/svg/specials-icon.svg';
import { ReactComponent as FlagIcon } from 'assets/images/svg/flag-2.svg';
import { ReactComponent as PaymentIcon } from 'assets/images/svg/payments.svg';
import { ReactComponent as StoreIcon } from 'assets/images/svg/icons/store-icon.svg';
import { ReactComponent as ParIcon } from 'assets/images/svg/par.svg';
import { ReactComponent as ChevronIcon } from 'assets/images/svg/chevron-right.svg';

import container from 'containers/HeaderMenuContainer';

import { CURRENT_LOCATION } from 'utils/constants';
import { features } from 'utils/features';
import * as routes from 'utils/routes';
import siteStyle, { M } from 'utils/siteStyle';
import { matchRoute } from 'utils/routeHelpers';
import { useDimension } from 'hooks/useDimensions';

const NavItemsDesktop = ({
  wrapperClassname,
  location,
  pathname,
  activeItem,
  setActiveItem,
  performSearch,
  setHotDealsSearch,
  setLocationFilter,
  setNearMeSearch,
  storeUrl,
}) => {
  const listMyCourseRef = useRef();
  const history = useHistory();
  const { width } = useDimension();
  const status = useSelector(({ site }) => site?.data);

  const GolfClubLabel = (M.SG && 'Supreme Golf Club') || (M.BT && 'Golf Time Club');

  useEffect(() => {
    if (
      matchRoute(routes.EXPLORE_COUNTRY, pathname) || matchRoute(routes.EXPLORE_STATE, pathname)) {
      setActiveItem('explore');
    }
    if (!(
      matchRoute(routes.HOME, pathname)
      || matchRoute(routes.EXPLORE_COUNTRY, pathname)
      || matchRoute(routes.EXPLORE_STATE, pathname)
      || matchRoute(routes.CITY_SEARCH_RESULT, pathname)
      || matchRoute(routes.COURSE_SEARCH_RESULT, pathname)
      || matchRoute(routes.RATE_TYPES, pathname)
      || matchRoute(routes.SELECT_PROVIDER, pathname)
      || matchRoute(routes.CHECKOUT, pathname)
      || matchRoute(routes.BOOKING_CONFIRMATION, pathname)
    )) {
      setActiveItem('');
    }
  }, [activeItem, pathname, setActiveItem]);

  const selectNearMe = useCallback(() => {
    setLocationFilter({
      ...CURRENT_LOCATION,
      latitude: location.lat,
      longitude: location.lon,
      hierarchizedUrl: location.hierarchizedUrl,
    });
    setNearMeSearch();
    performSearch();
    setActiveItem('nearMe');
  }, [location, setLocationFilter, setActiveItem, setNearMeSearch, performSearch]);

  const selectDealsSearch = useCallback(() => {
    setLocationFilter({
      ...CURRENT_LOCATION,
      type: 'City',
      hierarchizedUrl: location.hierarchizedUrl,
      latitude: location.lat,
      longitude: location.lon,
    });
    setHotDealsSearch();
    performSearch('City');
    setActiveItem('deals');
  }, [setLocationFilter, setHotDealsSearch, setActiveItem, performSearch,
    location.hierarchizedUrl, location.lat, location.lon]);

  const renderNearMe = useMemo(() => {
    if (location && location.city && location.state) {
      return (
        <button
          id="navbar-menu-near-me"
          className={classnames(
            'flex items-center gap-2 py-sm px-4 rounded-2 cursor-pointer focus:outline-none',
            siteStyle({ gb: 'text-white', rg: 'text-white', default: 'text-black' }),
            { 'bg-white-two pointer-events-none !text-dark': activeItem === 'nearMe' },
          )}
          onClick={selectNearMe}
        >
          <LocationIcon className="w-5 h-5 text-inherit fill-current" />
          <p className="font-primary font-medium text-4 leading-4 -tracking-0.2 text-inherit">
            Near Me
          </p>
        </button>
      );
    }

    return (
      <NavHashLink
        id="navbar-menu-near-me"
        to="/#courses-near-me"
        className={siteStyle({
          common: 'flex items-center gap-2 py-sm px-4 rounded-2 cursor-pointer',
          gb: 'text-white',
          rg: 'text-white',
          default: 'text-dark',
        })}
        smooth
      >
        <LocationIcon className="w-5 h-5 text-inherit fill-current" />
        <p className="font-primary font-medium text-4 leading-4 -tracking-0.2 text-inherit">
          Near Me
        </p>
      </NavHashLink>
    );
  }, [location, activeItem, selectNearMe]);

  const renderDealsLink = useMemo(() => {
    if (location && location.city && location.state) {
      return (
        <button
          id="navbar-menu-top-deals"
          className={classnames(
            'flex items-center gap-2 py-sm px-4 rounded-2 cursor-pointer focus:outline-none',
            siteStyle({ gb: 'text-white', rg: 'text-white', default: 'text-black' }),
            { 'bg-white-two pointer-events-none !text-dark': activeItem === 'deals' },
          )}
          onClick={selectDealsSearch}
        >
          <SpecialsIcon className="w-5 h-5 text-inherit fill-current" />
          <p className="font-primary font-medium text-4 leading-4 -tracking-0.2 text-inherit">
            Specials
          </p>
        </button>
      );
    }

    return (
      <NavHashLink
        id="navbar-menu-top-deals"
        to="/#courses-near-me"
        className={siteStyle({
          common: 'flex items-center gap-2 py-sm px-4 rounded-2 cursor-pointer',
          gb: 'text-white',
          rg: 'text-white',
          default: 'text-dark',
        })}
        smooth
      >
        <SpecialsIcon className="w-5 h-5 text-inherit fill-current" />
        <p className="font-primary font-medium text-4 leading-4 -tracking-0.2 text-inherit">
          Specials
        </p>
      </NavHashLink>
    );
  }, [location, activeItem, selectDealsSearch]);

  const navItems = [
    {
      label: 'Search',
      id: 'search',
      Icon: SearchIcon,
      iconStyles: 'rotate-90',
      condition: true,
      isActive: activeItem === 'search',
    },
    {
      condition: features.nearMeNav,
      id: 'near-me',
      customComponent: renderNearMe,
    },
    {
      condition: features.topDeals,
      id: 'top-deals',
      customComponent: renderDealsLink,
    },
    {
      label: 'Explore',
      href: `${routes.EXPLORE_PREFIX}/united-states`,
      id: 'explore',
      Icon: FlagIcon,
      condition: features.exploreCountriesStates,
      isActive: activeItem === 'explore',
    },
    {
      label: 'Gift Cards',
      href: 'https://info.supremegolf.com/supreme-golf-gift-cards/',
      id: 'gift-cards',
      Icon: PaymentIcon,
      condition: features.giftCardLink,
      forSeo: true,
    },
    {
      label: 'Shop',
      href: storeUrl,
      id: 'shop',
      Icon: StoreIcon,
      condition: features.storeUrl && storeUrl,
      forSeo: true,
    },
    {
      label: 'Club',
      href: routes.NETWORK_MEMBERSHIP_SALES,
      id: 'membership',
      Icon: NationalMembershipIcon,
      condition: features.networkMembership && status?.networkMembershipEnabled,
      isActive: matchRoute(routes.NETWORK_MEMBERSHIP_SALES, pathname),
    },
  ];

  const forCoursesDropDown = {
    supremeGolf: [
      {
        customComponent: <a href="https://courses.supremegolf.com/tee-time-distribution/?utm_source=supremegolf.com&utm_medium=referral&utm_campaign=homepage">List My Course</a>,
        id: 'list-my-course',
        condition: features.rewardsAccumulation,
        external: true,
      },
      {
        customComponent: <a href="https://courses.supremegolf.com/supreme-golf-club/" target="_blank" rel="noopener noreferrer">{GolfClubLabel}</a>,
        id: 'national-membership',
        condition: features.networkMembership,
      },
    ],
    barstool: [
      {
        label: 'List My Course',
        id: 'list-my-course',
        href: routes.FREE_MARKETPLACE,
        condition: features.courseOperators,
      },
      {
        label: 'VIP Perks',
        id: 'vip-perks',
        href: routes.VIP_PERKS,
        condition: features.courseOperators && features.courseOperatorsVipPerks,
      },
      {
        label: 'Course Inquiries',
        id: 'golf-course-resources',
        href: routes.GOLF_COURSE_RESOURCES,
        condition: features.courseOperators,
      },
      {
        customComponent: <a href="https://courses.supremegolf.com/supreme-golf-club/" target="_blank" rel="noopener noreferrer">{GolfClubLabel}</a>,
        id: 'national-membership',
        condition: features.networkMembership,
      },
    ],
    golfDigest: [
      {
        label: 'List My Course',
        id: 'list-my-course',
        href: routes.FREE_MARKETPLACE,
        condition: features.courseOperators,
      },
    ],
  };

  const renderNavItem = (link) => {
    if (link?.customComponent && link.condition) return link?.customComponent;

    if (link?.forSeo) {
      return (
        <a
          id={`navbar-menu-${link?.id}`}
          href={link.href}
          target="_blank"
          rel="noopener noreferrer"
          className={siteStyle({
            common: 'flex items-center gap-2 py-sm px-4 rounded-2 cursor-pointer',
            gb: 'text-white',
            rg: 'text-white',
            default: 'text-dark',
          })}
        >
          <link.Icon className={classnames('w-5 h-5 text-inherit fill-current', link?.iconStyles)} />
          <p className="font-primary font-medium text-4 leading-4 -tracking-0.2 text-inherit">
            {link?.label}
          </p>
        </a>
      );
    }

    return link.condition && (
      <Link
        to={link.href || ''}
        id={`navbar-menu-${link?.id}`}
        className={classnames(
          'flex items-center gap-2 py-sm px-4 rounded-2 cursor-pointer',
          siteStyle({ gb: 'text-white', rg: 'text-white', default: 'text-dark' }),
          { 'bg-white-two !text-dark': link.isActive },
        )}
        onClick={link?.onClick || null}
      >
        {link.customIcon && link.customIcon}
        {link.Icon && <link.Icon className={classnames('w-5 h-5 text-inherit fill-current', link?.iconStyles)} />}
        <p className="font-primary font-medium text-4 leading-4 -tracking-0.2 text-inherit">
          {link?.label}
        </p>
      </Link>
    );
  };

  const renderDropDown = (
    <Dropdown
      id="for-courses"
      trigger={['click']}
      overlayClassName="z-50 w-40"
      overlay={(
        <Menu
          className="cursor-pointer"
        >
          {forCoursesDropDown[process.env.REACT_APP_NAME]?.map((element) => element.condition && (
            <MenuItem
              key={element.id}
              onClick={element?.customComponent ? null : () => history.push(element.href)}
              className="font-medium leading-4 text-3.5 -tracking-0.3 text-twelfth border-none"
            >
              {element?.customComponent || element.label}
            </MenuItem>
          ))}
        </Menu>
      )}
      animation="slide-up"
      placement="bottomLeft"
      getPopupContainer={() => listMyCourseRef.current}
    >
      <div
        className="flex items-center gap-2 cursor-pointer p-sm"
      >
        <ParIcon className="w-5 h-5 fill-current text-dark" />
        <p
          className={siteStyle({
            common: 'text-4 leading-4 -tracking-0.2 text-dark',
            // bt: 'font-korolav font-bold uppercase',
            default: 'font-primary font-medium',
          })}
        >
          For courses
        </p>
        <div className="w-5 h-5 flex items-center justify-items-center">
          <ChevronIcon className="w-full fill-current text-dark rotate-90" />
        </div>
      </div>
    </Dropdown>
  );

  return (
    <div className={classnames('flex items-center justify-between', wrapperClassname)}>
      <ul className={classnames({ 'list-none flex items-center gap-0': true, 'gap-2': width > 1080 })}>
        {navItems.map((link) => link.condition && <li key={`navbar-menu-${link?.id}`}>{renderNavItem(link)}</li>)}
      </ul>
      {(M.SG || features.courseOperators) && (
        <div className="flex items-center relative" ref={listMyCourseRef}>
          {renderDropDown}
        </div>
      )}
    </div>
  );
};

NavItemsDesktop.defaultProps = {
  wrapperClassname: '',
  storeUrl: '',
};

NavItemsDesktop.propTypes = {
  wrapperClassname: PropTypes.string,
  location: PropTypes.shape({
    city: PropTypes.string,
    state: PropTypes.string,
    lat: PropTypes.number,
    lon: PropTypes.number,
    hierarchizedUrl: PropTypes.string,
  }).isRequired,
  activeItem: PropTypes.string.isRequired,
  setActiveItem: PropTypes.func.isRequired,
  pathname: PropTypes.string.isRequired,
  performSearch: PropTypes.func.isRequired,
  setHotDealsSearch: PropTypes.func.isRequired,
  setLocationFilter: PropTypes.func.isRequired,
  setNearMeSearch: PropTypes.func.isRequired,
  storeUrl: PropTypes.string,
};

export default container(NavItemsDesktop);
